import React from 'react';
import {
  bgColors,
  borderColors,
  colors,
  getColorHexOrFallback,
  getTextColorFromBackgroundColor,
} from '@teetsh/app/src/components/colors';
import { ringColors } from '@teetsh/app/src/components/ringColors';
import tw, { css } from 'twin.macro';
import { Color } from '@teetsh/app/src/types/colors';
import { TagColorWithIntensity } from '@teetsh/app/src/styles/userConstants';
import ensureAccessibleContrastLightening from 'utils/style/ensureAccessibleContrastLightening';

type Size = 'xl' | 'lg' | 'md' | 'sm';

interface Props {
  elevation?: 0 | 1 | 2 | 3 | 4;
  children: React.ReactNode;
  className?: string;
  style?: object;
  interactive?: boolean;
  'data-testid'?: string;
  'aria-label'?: string;
  full?: boolean;
  role?: 'link' | 'presentation';
  tabIndex?: number;
  title?: string | React.ReactNode;
  onClick?: () => void;
  color?: Color | TagColorWithIntensity;
  print?: boolean;
  maxWidth?: Size;
  centered?: boolean;
  cardRef?: any;
  id?: string;
}

export const elevationShadows = {
  0: tw`shadow-none`,
  1: tw`shadow`,
  2: tw`shadow-lg`,
  3: tw`shadow-xl`,
  4: tw`shadow-2xl`,
};
export default function Card(props: Props) {
  const {
    elevation = 0,
    children,
    title,
    interactive,
    maxWidth,
    centered,
    role = 'presentation',
    full = false,
    color,
    print,
    id,
    onClick,
    cardRef,
    ...rest
  } = props;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events
    <div
      id={id}
      role={role}
      css={[
        tw`rounded-xl p-3 md:p-6 outline-none`,
        !color && tw`bg-white border border-neutral-quiet`,
        full && color && getTextColorFromBackgroundColor(color),
        color && bgColors(color),
        centered && tw`mx-auto`,
        color &&
          !full &&
          css`
            background-color: ${ensureAccessibleContrastLightening(
              getColorHexOrFallback(color),
              colors['text-neutral-quiet'],
            )};
          `,

        color && borderColors(color),
        color && !full && tw`border-l-8`,
        elevationShadows[elevation],
        maxWidth === 'xl' && tw`max-w-5xl`,
        maxWidth === 'lg' && tw`max-w-2xl`,
        maxWidth === 'md' && tw`max-w-lg`,
        maxWidth === 'sm' && tw`max-w-xs`,
        color && ringColors[color],
        interactive && tw`hover:border-neutral-moderate cursor-pointer`,
        color && interactive && tw`hover:ring-opacity-100`,
      ]}
      onClick={onClick}
      ref={cardRef}
      {...rest}
    >
      {title && <h2 tw="mb-6">{title}</h2>}
      {children}
    </div>
  );
}
